"use client"

import { useCallback, useEffect, useState } from "react"
import { deleteAccountAndData, fetchMe, loginUrl, signOut, type Me } from "@/lib/breach/cloud"

let cache: Me | null = null
const listeners = new Set<(m: Me) => void>()
const publish = (m: Me) => { cache = m; listeners.forEach((l) => l(m)) }

/** Shared account state for the whole page (one /api/auth/me request). */
export function useAccount(): { me: Me | null; refresh: () => Promise<void> } {
  const [me, setMe] = useState<Me | null>(cache)
  useEffect(() => {
    listeners.add(setMe)
    if (!cache) void fetchMe().then(publish)
    return () => { listeners.delete(setMe) }
  }, [])
  const refresh = useCallback(async () => { publish(await fetchMe()) }, [])
  return { me, refresh }
}

/** Sign-in control for the site nav. Renders nothing until sign-in is available. */
export function AccountMenu() {
  const { me, refresh } = useAccount()
  const [busy, setBusy] = useState(false)
  if (!me || !me.configured) return null

  if (!me.user) {
    const next = typeof window !== "undefined" ? window.location.pathname + window.location.search : "/incident"
    return (
      <a href={loginUrl(next)} className="rounded-md border border-border px-3 py-1.5 text-sm font-medium hover:bg-card">
        Sign in with Google
      </a>
    )
  }

  return (
    <details className="relative">
      <summary className="cursor-pointer list-none rounded-md border border-border px-3 py-1.5 text-sm">
        {me.user.email}{me.user.isProvider ? " · provider" : ""}
      </summary>
      <div className="absolute right-0 z-20 mt-2 w-72 rounded-lg border border-border bg-card p-3 text-sm shadow-lg">
        <p className="text-muted-foreground">Your incidents are saved to your account and this browser.</p>
        <button className="mt-3 w-full rounded-md border border-border px-3 py-1.5 text-left hover:bg-background" disabled={busy}
          onClick={async () => { setBusy(true); await signOut(); await refresh(); setBusy(false) }}>
          Sign out
        </button>
        <button className="mt-2 w-full rounded-md border border-destructive/60 px-3 py-1.5 text-left text-destructive hover:bg-background" disabled={busy}
          onClick={async () => {
            if (!confirm("Delete your account and every incident saved to it from our servers? Copies in this browser stay until you delete them. This cannot be undone.")) return
            setBusy(true); await deleteAccountAndData(); await refresh(); setBusy(false)
          }}>
          Delete my account and saved data
        </button>
      </div>
    </details>
  )
}

/** One-line save status for the incident page. */
export function SaveStatus({ syncing, syncError }: { syncing: boolean; syncError: string | null }) {
  const { me } = useAccount()
  if (!me || !me.configured) return <>Saved on this device only.</>
  if (me.user) {
    return <>{syncError ? <span className="text-destructive">{syncError} Your work is still saved on this device.</span> : syncing ? "Saving to your account…" : "Saved to your account and this device."}</>
  }
  const next = typeof window !== "undefined" ? window.location.pathname + window.location.search : "/incident"
  return <>Saved on this device only. <a className="underline" href={loginUrl(next)}>Sign in with Google</a> to save it to your account and pick it up on any device.</>
}
